import { Ellipses, Td } from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import ConfirmMenu from 'root/components/molecules/ConfirmMenu';
import OptionsMenu from 'root/components/molecules/OptionsMenu';
import { BOARD_DETAILS } from 'root/stores/fresh/schema';
import { EDIT_WORKSPACE } from 'root/stores/routes/APP_URLS';

import WorkspaceTeamList from '../WorkspaceTeamList';
import {
  StyledArchived,
  StyledConfirmMenu,
  StyledEditButton,
  StyledEditTd,
  StyledId,
  StyledMobilePrefix,
  StyledName,
  StyledNameTd,
  StyledTr,
} from './styles';

const WorkspaceRow = ({
  id,
  departmentId,
  name,
  archived,
  createdAt,
  mongoId,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const ref = useRef(null);
  const {
    doUpdateQuery,
    doDeactivateBoard,
    doSetFreshBoardKey,
    numberOfActiveBoards,
  } = useConnect(
    'doUpdateQuery',
    'doDeactivateBoard',
    'doSetFreshBoardKey',
    'selectNumberOfActiveBoards',
  );

  const options = archived
    ? []
    : [
        {
          onClick: () => {
            setShowMenu(false);
            doSetFreshBoardKey({
              boardIDs: [mongoId],
              fresh: false,
              key: BOARD_DETAILS,
            });
            doUpdateQuery({ boardId: mongoId, drawer: EDIT_WORKSPACE });
          },
          text: 'Edit',
        },
        {
          errorStyle: true,
          onClick: () => {
            setShowMenu(false);
            setShowConfirmDelete(true);
          },
          text: 'Deactivate',
        },
      ];

  return (
    <StyledTr archived={archived}>
      <StyledNameTd width="35%">
        <StyledName>
          {archived ? <StyledArchived>(Archived)</StyledArchived> : null}
          {name || '--'}
        </StyledName>
        <StyledId>{id ? `ID: ${mongoId}` : '--'}</StyledId>
      </StyledNameTd>
      <Td>
        <StyledMobilePrefix label="Department ID:">
          {departmentId || '--'}
        </StyledMobilePrefix>
      </Td>
      <Td>
        <StyledMobilePrefix label="Users Assigned:" />
        <WorkspaceTeamList archived={archived} boardId={mongoId} />
      </Td>
      <Td>
        <StyledMobilePrefix label="Created Date:">
          {createdAt &&
            new Date(createdAt).toLocaleDateString('en-US', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
        </StyledMobilePrefix>
      </Td>
      <StyledEditTd width={80}>
        {!archived && (
          <StyledEditButton
            data-test-id="120c091a-e39c-4244-bc1b-4cfc46d076a2"
            onClick={() => options.length > 0 && setShowMenu(!showMenu)}
            ref={ref}
            variant="empty"
          >
            <Ellipses />
          </StyledEditButton>
        )}

        <OptionsMenu
          onClose={() => setShowMenu(false)}
          options={options}
          targetRef={ref}
          visible={showMenu}
        />

        {numberOfActiveBoards === 1 ? (
          <StyledConfirmMenu
            actionButtonTitle="Contact Us"
            onActionButton={() => {
              window.location.href = 'mailto:support@usehatchapp.com';
            }}
            onCancel={() => setShowConfirmDelete(!showConfirmDelete)}
            showOnMobile
            subtitle={
              <p>
                You must have at{' '}
                <strong>least 1 active workspace on your account</strong>.
                Please contact support@usehatchapp.com if you want to deactivate
                all workspaces.
              </p>
            }
            targetRef={ref}
            title="Unable to Delete"
            visible={showConfirmDelete}
          />
        ) : (
          <ConfirmMenu
            actionButtonTitle="Deactivate"
            onActionButton={() => {
              setShowConfirmDelete(!showConfirmDelete);
              doDeactivateBoard({ mongoId, workspaceId: id });
            }}
            onCancel={() => setShowConfirmDelete(!showConfirmDelete)}
            showOnMobile
            subtitle="Are you sure you want to deactivate this workspace?"
            targetRef={ref}
            title="Deactivate Workspace"
            visible={showConfirmDelete}
          />
        )}
      </StyledEditTd>
    </StyledTr>
  );
};

WorkspaceRow.propTypes = {
  archived: PropTypes.bool,
  createdAt: PropTypes.string,
  departmentId: PropTypes.string,
  id: PropTypes.string.isRequired,
  mongoId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

WorkspaceRow.defaultProps = {
  archived: false,
  createdAt: null,
  departmentId: null,
};

export default WorkspaceRow;
